import React, { useState, useContext, useEffect } from "react";
import { useNavigate, createSearchParams } from "react-router-dom";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Modal from "common/Modal.jsx";
import Request from "common/Request";
import useProfile from "common/hooks/useProfile";
import BarPlot from "common/graphs/BarPlot";
import { PERIOD_TO_HOURS } from "common/constants";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { navigateWithQueryParams } from "../../../App";

const returnView = "viewStatusSubscriberGroups";

const ScopeGraphLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;

  .bar-plot {
    svg rect.bar {
      fill: ${(props) => props.color};
    }
    .x.axis tspan {
      font-size: 10px;
    }
  }
`;

const expressTargetRole = (role, grouping) =>
  role === "servers"
    ? "servers"
    : grouping === "ip-addresses"
    ? "client-ips"
    : grouping === "subscriber-ids"
    ? "subscribers-ids"
    : "client-ips";

const ScopeGraph = ({
  category,
  role,
  doClose,
  period,
  direction,
  categories,
  grouping = "",
  onClick,
  subscriberGroup,
}) => {
  const [request, setRequest] = useState(null);

  const doRequest = ({
    category,
    period,
    direction,
    categories,
    role,
    subscriberGroup,
  }) => {
    const hours = PERIOD_TO_HOURS[period];
    return ifCl
      .run(
        `show statistics dpi domain ${category} all ${expressTargetRole(
          role,
          grouping
        )} hours ${hours} lines ${categories} direction ${direction} subscriber-groups name ${subscriberGroup}`
      )
      .then(parseCategoryScope)
      .catch((error) => console.error("error", error));
  };;

  useEffect(() => {
    setRequest(doRequest({ category, period, direction, categories, role, subscriberGroup }));
  }, [category, period, direction, categories, role, grouping, subscriberGroup]);

  if (request === null) {
    return null;
  }

  return (
    <Request during={request}>
      {(result) => {
        return (
          <BarPlot
            className="bar-plot-graph"
            key={"label"}
            grid={true}
            valueLabel={labelPercent}
            yAxisLabel={labelPercentNoDecimals}
            items={asKeyAndPercent(result)}
            allYValues={[0, 100]}
            margin={{ top: 20, right: 1, bottom: 70, left: 75 }}
            onClick={(value) => onClick(value.key)}
          />
        );
      }}
    </Request>
  );
};

const _groupingChoices = [
  { value: "ip-addresses", label: "Clients by IP" },
  { value: "subscriber-ids", label: "Clients by Subs-ID" },
];

const ClientsScopeGraph = ({
  category,
  doClose,
  period,
  direction,
  categories,
  subscriberGroup
}) => {
  const [grouping, setGrouping] = useState("ip-addresses");
  const navigate = useNavigate();

  const handleClick = (key) => {
    const target = grouping === "subscriber-ids" ? "subsId" : "addr";
    navigate({
      pathname: "/viewSubsDetail",
      search: createSearchParams({ returnView, [target]: key }).toString(),
    });
  };

  return (
    <ScopeGraphLayout color={category.color}>
      <h4>Main clients</h4>
      <small>
        Click on each IP address or subscriber-id to see detailed subscriber
        information.
      </small>
      <ScopeGraph
        role="clients"
        category={category.name}
        direction={direction}
        period={period}
        categories={categories}
        grouping={grouping}
        onClick={handleClick}
        subscriberGroup={subscriberGroup}
      />
      <div className="input-group centered">
        <SelectInputWithIcon
          title="Output grouping"
          name="Output grouping"
          selected={grouping}
          onChange={({ target }) => setGrouping(target.value)}
          options={_groupingChoices}
        />
      </div>
    </ScopeGraphLayout>
  );
};

const parseCategoryScope = (response) => {
  const [_head, ...rows] = response.trim("\n").split("\n");
  const labelAndValue = (line) => {
    const [label, valueStr] = line.split(/\s+/);
    return [label, Number(valueStr)];
  };
  const items = rows.map(labelAndValue);
  const [_totalLabel, total] = items.pop();
  const top10Items = items.sort((a, b) =>  b[1] - a[1]).slice(0,10);
  return { items: top10Items, total };
};

const labelPercentWithPrecission =
  (precision = 2) =>
  (d) =>
    d.value === null ? "--" : `${d.value.toFixed(precision)}%`;

const labelPercent = labelPercentWithPrecission(2);
const labelPercentNoDecimals = labelPercentWithPrecission(0);

const asKeyAndPercent = ({ items = [], total = 1 }) =>
  items.map(([key, value]) => ({ key, value: (value / total) * 100 }));

const ServersScopeGraph = ({
  category,
  doClose,
  period,
  direction,
  categories,
  subscriberGroup,
}) => {
  const navigate = useNavigate();
  const handleClick = (key) => {
    navigate({pathname: "/viewDPILatency", search: createSearchParams({
      returnView,
      destination: key,
    }).toString()})
  };

  return (
    <ScopeGraphLayout color={category.color}>
      <h4>Main servers</h4>
      <small>Click on each IP address to see uplink latency information</small>
      <ScopeGraph
        role="servers"
        category={category.name}
        direction={direction}
        period={period}
        categories={categories}
        onClick={handleClick}
        subscriberGroup={subscriberGroup}
      />
    </ScopeGraphLayout>
  );
};

const CategoryDetailsModal = ({
  category,
  setCategory,
  period,
  direction,
  categories,
  subscriberGroup,
}) => {
  const profile = useProfile() || {};
  const areClientsInspectable = profile.dpiEnabled === true;
  const doClose = () => setCategory(null);
  if (category === null) {
    return null;
  }
  return (
    <Modal
      id="categoryInspectionModal"
      large={true}
      title={
        areClientsInspectable
          ? `PERCENTAGE OF TRAFFIC FOR MAIN CLIENTS AND SERVERS IN ${category.name}`
          : `PERCENTAGE OF TRAFFIC FOR MAIN SERVERS IN ${category.name}`
      }
      subTile={
        areClientsInspectable
          ? "Clients are IP addresses on the access side and servers are IP addresses on the Internet side"
          : "Servers are IP addresses on the Internet side"
      }
      onClose={doClose}
      content={() => (
        <>
          <ServersScopeGraph
            doClose={doClose}
            category={category}
            period={period}
            direction={direction}
            categories={categories}
            subscriberGroup={subscriberGroup}
          />
          {areClientsInspectable !== true ? null : (
            <ClientsScopeGraph
              doClose={doClose}
              category={category}
              period={period}
              direction={direction}
              categories={categories}
              subscriberGroup={subscriberGroup}
            />
          )}
        </>
      )}
    />
  );
};

export default CategoryDetailsModal;
